// Client-portal administration API for the operator SPA: who signs in, what they see.
// Every call goes through the shared request helper — never raw fetch in components.
import { request } from './client.js';
import type { DeletionPlan } from './crm.js';
import type { Degradable } from '../../shared/problems.js';
import type { PortalObjectType as SharedObjectType, PortalViewId, Visibility as SharedVisibility } from '../../shared/portalViews.js';

// ───────────────────────── types ─────────────────────────

export type PortalRole = 'sponsor' | 'reviewer';

// The vocabulary is NOT re-declared here. It lives in src/shared/portalViews.ts, which the
// portal SPA renders from and the server gates on — a second copy of the object-type list is
// how a console starts describing a portal that does not exist (BL-4).
export { PORTAL_OBJECT_TYPES } from '../../shared/portalViews.js';
export type Visibility = SharedVisibility;
export type PortalObjectType = SharedObjectType;

export interface PortalGrant {
  engagement_id: number;
  code: string;
  name: string;
}

export interface PortalUser {
  id: number;
  client_id: number;
  contact_id: number | null;
  email: string;
  name: string;
  role: PortalRole;
  disabled: boolean;
  last_login_at: string | null;
  /** Most recent portal_audit row for this account — the honest "last seen". */
  last_seen_at: string | null;
  created_at: string;
  activated: boolean;
  invite_pending: boolean;
  invite_expires_at: string | null;
  invite_expired: boolean;
  client_name: string;
  contact_name: string | null;
  grants: PortalGrant[];
  audit_events: number;
  granted_here?: boolean;
}

export interface InviteResult {
  portal_user_id: number;
  email: string;
  name: string;
  invite_url: string;
  invite_path: string;
  expires_at: string;
  expires_in_days: number;
  reissued: boolean;
  notice: string;
  /** §3.6 — an invite into a switched-off portal is dead on arrival, and now it says so. */
  warning: string | null;
}

export interface PublicationResponse {
  engagement: { id: number; code: string; name: string; portal_enabled: boolean };
  object_types: readonly string[];
  settings: Record<PortalObjectType, Visibility>;
}

export interface PublishableRow {
  id: number;
  label: string;
  status: string;
  published: boolean;
  detail: string | null;
  /** requirements only: type of the requirement (out_of_scope is an exclusion). */
  type?: string | null;
  /** Descoped or out-of-scope — never swept up by "publish all". */
  excluded?: boolean;
}
export interface PublishableGroup {
  table: string;
  object_type: PortalObjectType;
  rows: PublishableRow[];
}
export interface PublishableResponse {
  engagement_id: number;
  settings: Record<PortalObjectType, Visibility>;
  objects: Record<string, PublishableGroup>;
}

export interface PreviewContext {
  engagement: { id: number; code: string; name: string };
  preview_url: string;
  preview_path: string;
  expires_at: string;
  notice: string;
}

export interface PortalComment {
  id: number;
  object_type: string;
  object_id: number;
  /** Human ref of the commented object (REQ-014, CR-004…) when it has one. */
  object_ref?: string | null;
  body: string;
  created_at: string;
  author: string;
  author_kind: 'portal' | 'operator';
}

// ───────────────────────── the projection (§3.2) ─────────────────────────
//
// The mirror of src/server/utils/projectionQueries.ts. One payload, one poll, and the only
// figure on the page that moves without the operator touching anything (CLIENT LAST SEEN).

export interface ProjectionView {
  id: PortalViewId;
  label: string;
  needs: PortalObjectType | null;
  visibility: Visibility | null;
  /** The tab is in the client's navigation. */
  appears: boolean;
  /** It renders, and finds nothing behind it. */
  empty: boolean;
  /** What the client would see, under the PORTAL's own predicate for this view. */
  published: number;
  total: number;
  capability: string;
}

export interface TypeCounts {
  total: number;
  published: number;
  in_scope: number;
  drafts_published: number;
  excluded: number;
  excluded_published: number;
  /** Differs from `published` where the portal applies more than the flag (UAT wants `ready`). */
  portal_visible: number;
}

export interface DriftRow { table: string; ref: string; revised_at: string; published_at: string }
export interface RefRow { table: string; ref: string; status: string }
export interface AwaitingRow {
  object_type: string; object_id: number; ref: string | null; who: string | null; days: number; at: string;
}

export interface Projection extends Degradable {
  engagement: {
    id: number; code: string; name: string;
    client_id: number; client_name: string; phase: string; portal_enabled: boolean;
  };
  matrix: Record<PortalObjectType, Visibility>;
  views: ProjectionView[];
  counts: Record<PortalObjectType, TypeCounts>;
  shared: { n: number; total: number };
  people: { accounts: number; active: number; sponsors: number; invite_pending: number };
  last_client_action: { at: string; days: number; who: string; action: string } | null;
  awaiting: AwaitingRow[];
  drift: DriftRow[];
  draft_published: RefRow[];
  published_exclusions: RefRow[];
}

// ───────────────────────── helpers ─────────────────────────

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });
const patch = (body: unknown): RequestInit => ({ method: 'PATCH', body: JSON.stringify(body) });
const put = (body: unknown): RequestInit => ({ method: 'PUT', body: JSON.stringify(body) });

// ───────────────────────── api ─────────────────────────

export const portalAdminApi = {
  /** The whole projection in one call — the page's only poll. */
  projection: (engagementId: number) =>
    request<Projection>(`/portal-admin/projection${qs({ engagement_id: engagementId })}`),

  users: (engagementId: number) => request<PortalUser[]>(`/portal-admin/users${qs({ engagement_id: engagementId })}`),

  /** `warning` is set when the portal is off: the account exists and can reach nothing (§3.6). */
  createUser: (body: { engagement_id: number; email: string; name: string; role: PortalRole; contact_id?: number | null }) =>
    request<PortalUser & { warning: string | null }>('/portal-admin/users', json(body)),

  updateUser: (
    id: number,
    body: Partial<{ name: string; role: PortalRole; disabled: boolean; contact_id: number | null; engagement_id: number }>,
  ) =>
    request<PortalUser>(`/portal-admin/users/${id}`, patch(body)),

  /**
   * What removing this account would take with it, and why it might refuse. An account that
   * signed, ran a test, raised a defect or decided a CR is disabled instead — the plan says
   * so in those words.
   */
  userDeletionPlan: (id: number) => request<DeletionPlan>(`/portal-admin/users/${id}/deletion-plan`),

  deleteUser: (id: number, engagementId?: number) =>
    request<{ ok: boolean }>(`/portal-admin/users/${id}${qs({ engagement_id: engagementId })}`, { method: 'DELETE' }),

  invite: (id: number, engagementId?: number) =>
    request<InviteResult>(`/portal-admin/users/${id}/invite`, json({ engagement_id: engagementId })),

  revokeInvite: (id: number) => request<{ ok: boolean }>(`/portal-admin/users/${id}/invite`, { method: 'DELETE' }),

  addGrant: (id: number, engagementId: number) =>
    request<{ ok: boolean; warning: string | null }>(`/portal-admin/users/${id}/grants`, json({ engagement_id: engagementId })),

  removeGrant: (id: number, engagementId: number) =>
    request<{ ok: boolean }>(`/portal-admin/users/${id}/grants/${engagementId}`, { method: 'DELETE' }),

  publication: (engagementId: number) => request<PublicationResponse>(`/portal-admin/publication${qs({ engagement_id: engagementId })}`),

  savePublication: (engagementId: number, settings: Partial<Record<PortalObjectType, Visibility>>) =>
    request<{ engagement_id: number; settings: Record<PortalObjectType, Visibility> }>(
      '/portal-admin/publication',
      put({ engagement_id: engagementId, settings }),
    ),

  setPortalEnabled: (engagementId: number, enabled: boolean) =>
    request<{ id: number; code: string; portal_enabled: boolean }>(
      '/portal-admin/portal-enabled',
      json({ engagement_id: engagementId, enabled }),
    ),

  publishable: (engagementId: number) => request<PublishableResponse>(`/portal-admin/publishable${qs({ engagement_id: engagementId })}`),

  /**
   * engagementId says WHICH engagement's portal a DOCUMENT publish is for — documents are
   * client-owned and their published flag lives on the engagement link. Optional while a
   * document has a single link; required once it is linked to several engagements.
   */
  publish: (table: string, ids: number[], published: boolean, engagementId?: number) =>
    request<{ table: string; published: boolean; updated: { id: number; label: string }[] }>(
      '/portal-admin/publish',
      json({ table, ids, published, ...(engagementId ? { engagement_id: engagementId } : {}) }),
    ),

  previewContext: (engagementId: number) => request<PreviewContext>(`/portal-admin/preview-context${qs({ engagement_id: engagementId })}`),

  comments: (engagementId: number) => request<PortalComment[]>(`/portal-admin/comments${qs({ engagement_id: engagementId })}`),

  reply: (body: { engagement_id: number; object_type: string; object_id: number; body: string }) =>
    request<PortalComment>('/portal-admin/comments', json(body)),
};
